// ASNP 2018 classification: PC → score 1-7
const classifyPC = (pc) => {
  if (pc < 2) return 1;
  if (pc <= 5) return 2;
  if (pc < 16) return 3;
  if (pc <= 84) return 4;
  if (pc < 95) return 5;
  if (pc <= 98) return 6;
  return 7;
};

// ASNP 2018 classification: NS → score 1-7
const classifyNS = (ns) => {
  if (ns < 4) return 1;
  if (ns <= 5) return 2;
  if (ns < 7) return 3;
  if (ns <= 13) return 4;
  if (ns < 15) return 5;
  if (ns <= 16) return 6;
  return 7;
};

// ASNP 2018 classification: z-score → score 1-7
const classifyZ = (z) => {
  if (z < -2.0) return 1;
  if (z <= -1.6) return 2;
  if (z < -1.0) return 3;
  if (z <= 1.0) return 4;
  if (z < 1.6) return 5;
  if (z <= 2.0) return 6;
  return 7;
};

// Detect value type from label. PR (Prozentrang) is the German equivalent
// of PC (percentile) and uses the same classification thresholds.
const getValueType = (label) => {
  const lower = label.toLowerCase();
  if (lower === "comment") return "comment";
  if (/\bns\b/.test(lower)) return "NS";
  if (lower.includes(" z") || lower.endsWith("z") || lower.includes("z ")) return "z";
  if (lower.includes("pc")) return "PC";
  if (/\bpr\b/.test(lower)) return "PC";
  return "unknown";
};

// Parse raw value strings into a [low, high] bound pair. Handles numbers,
// "< X", "> X" (single-sided, low === high), and "X-Y" ranges (real spans,
// low !== high) — the range case is what lets computeEvaluation detect a
// value straddling two ASNP categories instead of silently keeping only the
// lower bound.
const parseScoreRange = (rawValue) => {
  if (typeof rawValue === "number") return { low: rawValue, high: rawValue };
  if (typeof rawValue !== "string") return null;

  const trimmed = rawValue.trim();

  // "< X" → X - 0.5
  const ltMatch = trimmed.match(/^<\s*(.+)/);
  if (ltMatch) {
    const num = parseFloat(ltMatch[1]);
    return isNaN(num) ? null : { low: num - 0.5, high: num - 0.5 };
  }

  // "> X" → X + 0.5
  const gtMatch = trimmed.match(/^>\s*(.+)/);
  if (gtMatch) {
    const num = parseFloat(gtMatch[1]);
    return isNaN(num) ? null : { low: num + 0.5, high: num + 0.5 };
  }

  // "X-Y" range (not negative numbers) → real [low, high] span
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    const lower = parseFloat(rangeMatch[1]);
    const upper = parseFloat(rangeMatch[2]);
    return isNaN(lower) || isNaN(upper) ? null : { low: lower, high: upper };
  }

  // Plain number (including negatives like "-1.2")
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : { low: num, high: num };
};

// Map score (1-7) to Excel evaluation column
const scoreToColumn = (score) => {
  const map = { 1: "I", 2: "J", 3: "K", 4: "L" };
  if (score >= 5) return "M";
  return map[score] || "L";
};

// Parse a clock-test score expressed as "X/Y", "X out of Y", "X outta Y",
// or a plain number (assumed out of 10). Returns the fraction X/Y, or null.
const parseClockFraction = (raw) => {
  if (typeof raw === "number") return raw / 10;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const m = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:\/|out\s*of|outta)\s*(\d+(?:\.\d+)?)$/i);
  if (m) {
    const num = parseFloat(m[1]);
    const denom = parseFloat(m[2]);
    if (!denom) return null;
    return num / denom;
  }
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : num / 10;
};

// Manos clock test (1999): score out of 10.
// = 8/10 → "3"          → column K
// < 8/10 → "1_2"        → column I (merged I-J at row 25)
// > 8/10 → "4_5_6_7"    → column L (merged L-N at row 25)
// German Uhrentest is scored /7 with no fixed cutoff: the clinician marks the
// evaluation by hand, so return null (no auto-cross) for de.
const computeClockManosEval = (resultData, resultLoc, opts = {}) => {
  if (opts.lang === "de") return null;
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.kind === "scoreClock");
  const fraction = item ? parseClockFraction(item.value) : null;
  if (fraction === null) {
    return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  const threshold = 0.8;
  if (fraction === threshold) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  if (fraction < threshold) return { mark: "X", scoreValue: "1_2", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// BREF (Appollonio, 2005): total score out of 18.
// < 15 → "1_2"   → column I (merged I-J)
// = 15 → "3"     → column K
// > 15 → "5_6_7" → column M (merged M-N)
const computeBrefEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.kind === "brefScore" && d.name === "total");
  const total = item ? parseFloat(item.value) : NaN;
  if (!Number.isFinite(total)) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (total < 15) return { mark: "X", scoreValue: "1_2", loc: `I${row}` };
  if (total === 15) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  return { mark: "X", scoreValue: "5_6_7", loc: `M${row}` };
};

// DTLA (Mariage et al., 2017): total score out of 100.
// Thresholds depend on age band (50–64 vs 65–80) and education (≤11 vs ≥12 ans).
const DTLA_2017_THRESHOLDS = {
  "50_64_low":  { seuil: 84, cutoff: 78 },
  "50_64_high": { seuil: 94, cutoff: 85 },
  "65_80_low":  { seuil: 83, cutoff: 75 },
  "65_80_high": { seuil: 92, cutoff: 81 },
};

export const getDtla2017Thresholds = (age, anneesEtudes) => {
  const ageNum = typeof age === "number" ? age : parseInt(age, 10);
  const eduNum = parseFloat(anneesEtudes);
  if (!Number.isFinite(ageNum) || !Number.isFinite(eduNum)) return null;
  let ageKey;
  if (ageNum >= 50 && ageNum <= 64) ageKey = "50_64";
  else if (ageNum >= 65 && ageNum <= 80) ageKey = "65_80";
  else return null;
  const eduKey = eduNum <= 11 ? "low" : "high";
  return DTLA_2017_THRESHOLDS[`${ageKey}_${eduKey}`];
};

export const getDtla2017Status = (score, thresholds) => {
  if (!thresholds || !Number.isFinite(score)) return null;
  if (score < thresholds.cutoff) return "en dessous du seuil";
  if (score < thresholds.seuil) return "limite";
  return "en dessus du cutoff";
};

// < cutoff       → "1_2"     → column I (merged I-J)
// cutoff ≤ x < seuil → "3"   → column K
// ≥ seuil        → "4_5_6_7" → column L (merged L-M)
const computeDtla2017Eval = (resultData, resultLoc, opts) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const score = item ? parseFloat(item.value) : NaN;
  const thr = getDtla2017Thresholds(opts.age, opts.anneesEtudes);
  if (!Number.isFinite(score) || !thr) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (score < thr.cutoff) return { mark: "X", scoreValue: "1_2", loc: `I${row}` };
  if (score < thr.seuil)  return { mark: "X", scoreValue: "3",   loc: `K${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// HADS-D (Herrmann et al., 2011): Angst /21 + Depression /21. Worse band wins.
// < 8  → "4" → column L (nicht signifikant)
// 8-9  → "3" → column K (grenzwertig)
// ≥ 10 → "2" → column J (signifikant)
const computeHadsDEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const bandOf = (s) => {
    if (!Number.isFinite(s)) return null;
    if (s < 8) return 4;
    if (s < 10) return 3;
    return 2;
  };
  const angst = parseFloat(resultData.find((d) => d.name === "angst")?.value);
  const dep = parseFloat(resultData.find((d) => d.name === "depression")?.value);
  const bands = [bandOf(angst), bandOf(dep)].filter((b) => b !== null);
  if (bands.length === 0) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  const worst = Math.min(...bands);
  if (worst === 2) return { mark: "X", scoreValue: "2", loc: `J${row}` };
  if (worst === 3) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  return { mark: "X", scoreValue: "4", loc: `L${row}` };
};

// DIA-S (Heidenblut & Zank, 2010): score out of 10.
// < 3 → "4" → column L (unauffällig)
// = 3 → "3" → column K (Vd.a. depressive Symptomatik)
// ≥ 4 → "2" → column J (klinisch relevante depressive Symptomatik)
const computeDiaSEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const score = item ? parseFloat(item.value) : NaN;
  if (!Number.isFinite(score)) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (score < 3) return { mark: "X", scoreValue: "4", loc: `L${row}` };
  if (score === 3) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  return { mark: "X", scoreValue: "2", loc: `J${row}` };
};

// MoCA (Nasreddine, 2005): total score out of 30, cutoff 26.
// < 26 → "1_2"     → column I (merged I-J)
// = 26 → "3"       → column K
// > 26 → "4_5_6_7" → column L (merged L-M)
const computeMocaEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const total = item ? parseFloat(item.value) : NaN;
  if (!Number.isFinite(total)) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (total < 26) return { mark: "X", scoreValue: "1_2", loc: `I${row}` };
  if (total === 26) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Generic single-cutoff score evaluation: opts.evalCutoff defines the threshold.
// < cutoff → "1_2"     → column I (merged I-J)
// = cutoff → "3"       → column K
// > cutoff → "4_5_6_7" → column L (merged L-M)
const computeScoreCutoffEval = (resultData, resultLoc, opts) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const score = item ? parseFloat(item.value) : NaN;
  const cutoff = parseFloat(opts.evalCutoff);
  if (!Number.isFinite(score) || !Number.isFinite(cutoff)) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (score < cutoff) return { mark: "X", scoreValue: "1_2", loc: `I${row}` };
  if (score === cutoff) return { mark: "X", scoreValue: "3", loc: `K${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// BBEP — Macoir et al. (2025): simplified clinical cutoffs (1st percentile),
// raw score thresholds by age band 50-69 vs 70-89. ≤ cutoff → below-average
// (merged I-J-K, "1_2_3"); above → average (merged L-M, "4_5_6_7").
const BBEP_MACOIR_2025_CUTOFFS = {
  symbolic:    { "50_69": 3, "70_89": 2 },
  pantomimes:  { "50_69": 7, "70_89": 8 },
  meaningless: { "50_69": 6, "70_89": 4 },
};

const getBbepMacoir2025AgeBand = (age) => {
  const a = typeof age === "number" ? age : parseInt(age, 10);
  if (!Number.isFinite(a)) return null;
  if (a >= 50 && a <= 69) return "50_69";
  if (a >= 70 && a <= 89) return "70_89";
  return null;
};

export const getBbepMacoir2025Cutoff = (subtest, age) => {
  const band = getBbepMacoir2025AgeBand(age);
  const subtable = BBEP_MACOIR_2025_CUTOFFS[subtest];
  if (!band || !subtable) return null;
  return subtable[band];
};

const computeBbepMacoir2025Eval = (resultData, resultLoc, opts) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const score = item ? parseFloat(item.value) : NaN;
  const cutoff = getBbepMacoir2025Cutoff(opts.bbepSubtest, opts.age);
  if (!Number.isFinite(score) || cutoff == null) {
    return { mark: "O", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  if (score <= cutoff) return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Bells Test (Rousseaux): pathological if any flag is set in the bellsResult.
// pathological → "1_2_3" → column I, otherwise "4_5_6_7" → column L.
const computeBellsRousseauxEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const flagItem = resultData.find((d) => d.name === "_bellsPath");
  if (!flagItem) {
    return { mark: "O", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  if (flagItem.value === "1") return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// VOSP (Warrington & James, 1991): 5% cut-off scores per subtest, age <50 vs ≥50.
const VOSP_CUTOFFS = {
  screening:              { lt50: 15, gte50: 15 },
  lettresFragmentees:     { lt50: 17, gte50: 16 },
  denombrementPoints:     { lt50: 8,  gte50: 8  },
  discriminationPosition: { lt50: 18, gte50: 18 },
  localisationChiffres:   { lt50: 7,  gte50: 7  },
  analysesCubes:          { lt50: 6,  gte50: 6  },
};

export const getVospCutoff = (paramName, age) => {
  const c = VOSP_CUTOFFS[paramName];
  if (!c) return null;
  const ageNum = typeof age === "number" ? age : parseFloat(age);
  return Number.isFinite(ageNum) && ageNum >= 50 ? c.gte50 : c.lt50;
};

export const getVospStatus = (resultData, age) => {
  let entered = 0;
  let belowCutoff = 0;
  for (const item of resultData || []) {
    const cutoff = getVospCutoff(item.name, age);
    if (cutoff == null) continue;
    const v = parseFloat(String(item.value).replace(",", "."));
    if (!Number.isFinite(v)) continue;
    entered++;
    if (v < cutoff) belowCutoff++;
  }
  if (entered === 0) return null;
  return belowCutoff > 0 ? "fail" : "pass";
};

// VOSP: any entered subtest below cutoff → "1_2_3" (merged I-K),
// otherwise → "4_5_6_7" (merged L-N).
const computeVospEval = (resultData, resultLoc, opts) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const status = getVospStatus(resultData, opts.age);
  if (status === null) {
    return { mark: "O", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  if (status === "fail") return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Bissection de lignes (Ferber, 2001): mean deviation %, cutoff 5%.
// > 5%  → "1_2_3"   → column I (merged I-K)
// ≤ 5%  → "4_5_6_7" → column L (merged L-N)
const computeBissectionFerberEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const dev = resultData.find((d) => d.name === "deviation");
  const num = dev ? parseFloat(String(dev.value).replace(",", ".")) : NaN;
  if (!Number.isFinite(num)) {
    return { mark: "O", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  if (num > 5) return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Apples Test (Humphreys): combined cut-off rule across three measures.
// Normal (cibles >42 AND diff G-D ≤2 AND asymétrie ≤1) → "4_5_6_7" → column L (merged L-M).
// Any measure breaching its cut-off → deficit → "1_2_3" → column I (merged I-K).
const computeApplesHumphreysEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const num = (name) => {
    const d = resultData.find((x) => x.name === name);
    return d ? parseFloat(String(d.value).replace(",", ".")) : NaN;
  };
  const cibles = num("cibles");
  const diff = num("diffGD");
  const asym = num("asymetrie");
  if (![cibles, diff, asym].some(Number.isFinite)) {
    return { mark: "O", scoreValue: "4_5_6_7", loc: `L${row}` };
  }
  const breach =
    (Number.isFinite(cibles) && cibles <= 42) ||
    (Number.isFinite(diff) && diff > 2) ||
    (Number.isFinite(asym) && asym > 1);
  if (breach) return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Appariement formes géométriques (TGV, 2020): score out of 12.
// 12/12 (PC > 50)   → merged L-M → loc L
// 11/12 (PC 10-20)  → merged K-L → loc K
// 10/12 (PC = 5)    → cell J     → loc J
// < 10  (PC < 1)    → cell I     → loc I
const computeTgvScoreEval = (resultData, resultLoc) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  const item = resultData.find((d) => d.name === "score");
  const score = item ? parseInt(item.value, 10) : NaN;
  if (!Number.isFinite(score)) {
    return { mark: "O", scoreValue: "4", loc: `L${row}` };
  }
  if (score >= 12) return { mark: "X", scoreValue: "4_5",   loc: `L${row}` };
  if (score === 11) return { mark: "X", scoreValue: "3_4",  loc: `K${row}` };
  if (score === 10) return { mark: "X", scoreValue: "2",    loc: `J${row}` };
  return { mark: "X", scoreValue: "1", loc: `I${row}` };
};

// NOSGER (Spiegel et al., 1999) and BDQ (Rascovsky 2011): any dimension
// at/above its sex-specific cut-off → "auffällig" (loc I, merged I-K). All
// dimensions decided below cut-off → "unauffällig" (loc L, merged L-N).
// Prefer structured flags from calcNosger/calcBDQ (opts.anyDecided/
// opts.anyAuffaellig); fall back to value-text substring sniffing for legacy
// callers that don't pass them.
const computeNosgerEval = (resultData, resultLoc, opts = {}) => {
  const row = resultLoc.replace(/[A-Z]/g, "");
  let anyDecided = opts.anyDecided;
  let anyAuffaellig = opts.anyAuffaellig;
  if (anyDecided === undefined || anyAuffaellig === undefined) {
    anyDecided = false;
    anyAuffaellig = false;
    for (const d of resultData || []) {
      const v = String(d.value || "").toLowerCase();
      if (v.includes("auffällig") || v.includes("unauffällig") || v.includes("anormal") || v.includes("normal")) {
        anyDecided = true;
        if (v.startsWith("auffällig") || v.startsWith("anormal")) anyAuffaellig = true;
      }
    }
  }
  // Sex unknown → no dimension decided. Leave the eval row blank ("O" was
  // misleading because it implied a clinically-normal observation).
  if (!anyDecided) return null;
  if (anyAuffaellig) return { mark: "X", scoreValue: "1_2_3", loc: `I${row}` };
  return { mark: "X", scoreValue: "4_5_6_7", loc: `L${row}` };
};

// Compute evaluation from resultData using ASNP 2018 rules.
// The lowest non-italic score determines the mark position.
// Items with `italic: true` are excluded from the minimum calculation.
// Mark: "X" for standardized (PC/NS/z values present), "O" for clinical only.
export const computeEvaluation = (resultData, resultLoc, opts = {}) => {
  if (opts.evalRule === "clockManos") {
    return computeClockManosEval(resultData, resultLoc, opts);
  }
  if (opts.evalRule === "brefTotal") {
    return computeBrefEval(resultData, resultLoc);
  }
  if (opts.evalRule === "mocaTotal") {
    return computeMocaEval(resultData, resultLoc);
  }
  if (opts.evalRule === "diaS") {
    return computeDiaSEval(resultData, resultLoc);
  }
  if (opts.evalRule === "hadsD") {
    return computeHadsDEval(resultData, resultLoc);
  }
  if (opts.evalRule === "dtla2017") {
    return computeDtla2017Eval(resultData, resultLoc, opts);
  }
  if (opts.evalRule === "scoreCutoff") {
    return computeScoreCutoffEval(resultData, resultLoc, opts);
  }
  if (opts.evalRule === "bbepMacoir2025") {
    return computeBbepMacoir2025Eval(resultData, resultLoc, opts);
  }
  if (opts.evalRule === "bissectionFerber") {
    return computeBissectionFerberEval(resultData, resultLoc);
  }
  if (opts.evalRule === "applesHumphreys") {
    return computeApplesHumphreysEval(resultData, resultLoc);
  }
  if (opts.evalRule === "bellsRousseaux") {
    return computeBellsRousseauxEval(resultData, resultLoc);
  }
  if (opts.evalRule === "vosp") {
    return computeVospEval(resultData, resultLoc, opts);
  }
  if (opts.evalRule === "tgvScore") {
    return computeTgvScoreEval(resultData, resultLoc);
  }
  if (opts.evalRule === "nosger" || opts.evalRule === "bdq") {
    return computeNosgerEval(resultData, resultLoc, opts);
  }
  const scores = [];
  let hasStandardized = false;

  for (const item of resultData) {
    if (item.italic) continue;

    const type = getValueType(item.label);
    if (type === "comment" || type === "unknown") continue;

    const range = parseScoreRange(item.rawValue ?? item.value);
    if (range === null) continue;

    hasStandardized = true;

    const classify = type === "PC" ? classifyPC : type === "NS" ? classifyNS : type === "z" ? classifyZ : null;
    if (!classify) continue;

    scores.push({ low: classify(range.low), high: classify(range.high) });
  }

  const mark = hasStandardized ? "X" : "O";
  const row = resultLoc.replace(/[A-Z]/g, "");

  if (scores.length === 0) {
    return null;
  }

  // The lowest item determines the score position. When that item is itself
  // a range straddling categories (e.g. empan PC "0-4", RL/RI PC "5-25"),
  // widen the mark to a merged span instead of silently keeping only the
  // low bound. Ties at the lowest category take the widest span among them.
  const minLow = Math.min(...scores.map((s) => s.low));
  const maxHigh = Math.max(...scores.filter((s) => s.low === minLow).map((s) => s.high));

  const colLow = scoreToColumn(minLow);
  const colHigh = scoreToColumn(maxHigh);

  if (colLow === colHigh) {
    const scoreValue = minLow >= 5 ? "5_6_7" : String(minLow);
    return { mark, scoreValue, loc: `${colLow}${row}` };
  }

  // minLow < 5 is guaranteed here: if minLow >= 5, colLow would already be
  // "M" (same as colHigh), which the branch above handles.
  const catList = [];
  for (let k = minLow; k <= maxHigh; k++) {
    if (k >= 5) { catList.push("5_6_7"); break; }
    catList.push(k);
  }

  return {
    mark,
    scoreValue: catList.join("_"),
    loc: `${colLow}${row}`,
    mergeRange: `${colLow}${row}:${colHigh}${row}`,
  };
};
